import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app from './reducers';
import Headroom from "headroom.js";
import { historyMiddleware } from './Middlewares/History.middleware';
import MiniCartContainer from './JobMeal/Containers/MiniCart.container';
import QuickSearchContainer from './JobMeal/Containers/QuickSearch.container';
import NavigationContainer from './JobMeal/Containers/Navigation.container';
import FacetedSearchContainer from './JobMeal/Containers/FacetedSearch.container';
import FacetedSearchCompactContainer from './JobMeal/Containers/FacetedSearchCompact.container';
import DynamicComponent from './Components/DynamicComponent';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
// use the parent page's store if possible, so the iframe will share the same store with the parent context.
// this to makes for example the Reorder button in My Page page to update the state and reload the mini cart
// when reorder action happens.
const store = window.__litium.store = window.__litium.store || (window.parent.__litium && window.parent.__litium.store)
    || createStore(app, preloadState, composeWithDevTools(applyMiddleware(thunk, historyMiddleware)));
window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    bootstrapBuyButtons: (node, tagAsAddOnSale) => bootstrapBuyButtons(node, tagAsAddOnSale),
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
}

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach(container => {
        ReactDOM.unmountComponentAtNode(container);
    });
}

const bootstrapComponents = () => {
    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCartContainer />
            </Provider>,
            document.getElementById('miniCart')
        );
    }
    if (document.getElementById('quickSearch')) {
        renderReact(
            <Provider store={store}>
                <QuickSearchContainer />
            </Provider>,
            document.getElementById('quickSearch')
        );
    }
    if (document.getElementById('navbar')) {
        renderReact(
            <Provider store={store}>
                <NavigationContainer />
            </Provider>,
            document.getElementById('navbar')
        );
    }
    if (document.getElementById('facetedSearch')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchContainer />
            </Provider>,
            document.getElementById('facetedSearch')
        );
    }
    if (document.getElementById('facetedSearchCompact')) {
        renderReact(
            <Provider store={store}>
                <FacetedSearchCompactContainer />
            </Provider>,
            document.getElementById('facetedSearchCompact')
        );
    }
    if (document.getElementById('myPagePersons')) {
        const PersonList = DynamicComponent({
            loader: () => import('./JobMeal/Containers/PersonList.container')
        });
        renderReact(
            <Provider store={store}>
                <PersonList />
            </Provider>,
            document.getElementById('myPagePersons')
        );
    }
    if (document.getElementById('myPageAddresses')) {
        const el = document.getElementById('myPageAddresses');
        if (el) {
            const faqDataAttr = el.getAttribute('data-faq');
            const faqLinks = faqDataAttr ? JSON.parse(faqDataAttr) : null;
            const AddressList = DynamicComponent({
                loader: () => import('./JobMeal/Containers/AddressList.container')
            });
            renderReact(
                <Provider store={store}>
                    <AddressList faqLinks={faqLinks} />
                </Provider>,
                el
            );
        }

    }
    if (document.getElementById('statisticsview')) {
        const el = document.getElementById('statisticsview');
        if (el) {
            const statisticsType = el.getAttribute('data-type');
            const StatisticsView = DynamicComponent({
                loader: () => import('./Containers/StatisticsView.container')
            });
            renderReact(
                <Provider store={store}>
                    <StatisticsView statisticsType={statisticsType} />
                </Provider>,
                el
            );
        }
    }
    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./JobMeal/Containers/Checkout.container')
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout')
        );
    }
    if (document.getElementById('serviceOrders')) {
        const ServiceOrders = DynamicComponent({
            loader: () => import('./JobMeal/Containers/ServiceOrders.container')
        });
        renderReact(
            <Provider store={store}>
                <ServiceOrders />
            </Provider>,
            document.getElementById('serviceOrders')
        );
    }
    if (document.getElementById('nextPlannedDeliveryBanner')) {
        const NextPlannedDelivery = DynamicComponent({
            loader: () => import('./JobMeal/Containers/NextPlannedDelivery.container')
        });
        renderReact(
            <Provider store={store}>
                <NextPlannedDelivery />
            </Provider>,
            document.getElementById('nextPlannedDeliveryBanner')
        );
    }
    if (document.getElementById('lightBoxImages')) {
        const LightboxImages = DynamicComponent({
            loader: () => import('./JobMeal/Containers/LightboxImages.container')
        });
        const rootElement = document.getElementById('lightBoxImages');
        const images = Array.from(rootElement.querySelectorAll('a')).map(img => ({ src: img.dataset.src }));
        const thumbnails = Array.from(rootElement.querySelectorAll('a img')).map(img => ({ src: img.src }));
        renderReact(
            <Provider store={store}>
                <LightboxImages images={images} thumbnails={thumbnails} />
            </Provider>,
            document.getElementById('lightBoxImages')
        );
    }

    if (document.querySelectorAll('.slider').length > 0) {
        const Slider = DynamicComponent({
            loader: () => import('./JobMeal/Components/Slider')
        });
        Array.from(document.querySelectorAll('.slider')).forEach((slider, index) => {
            const interval = slider.dataset.interval;

            const values = [...slider.querySelectorAll('.slider__block')].map(block => {
                return ({
                    image: block.dataset.image,
                    url: block.dataset.url,
                    linktext: block.dataset.linktext,
                    title: block.dataset.title,
                    text: block.dataset.text,
                    actionText: block.dataset.actionText,
                    textColor: block.dataset.textColor,
                })
            });
            if (values.length > 0) {
	            renderReact(
                    <Slider values={values} interval= {interval} />,
                    slider
                );
            }
        });
    }

    if (document.querySelector(".js-header--headroom")) {

        const headerHeight = document.querySelector(".js-header--headroom").offsetHeight;
        const header = document.querySelector(".js-header--headroom");
        var options = {
            offset: headerHeight
        };

        const headroom = new Headroom(header, options);
        headroom.init();

        const whenPageAtTopRemoveClass = () => {
            if (window.pageYOffset === 0) {
                header.classList.remove("headroom--pinned");
            }
        }
        window.onscroll = function () { whenPageAtTopRemoveClass() };
    }


    if (document.querySelectorAll('.js-tile-banner').length > 0) {
        const TileBanner = DynamicComponent({
            loader: () => import('./JobMeal/Components/TileBanner')
        });
        Array.from(document.querySelectorAll('.js-tile-banner')).forEach((tileBanner) => {
            const model = tileBanner.dataset.model ? JSON.parse(tileBanner.dataset.model) : undefined;
            renderReact(
                <TileBanner {...model} />,
                tileBanner
            );
        });
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./JobMeal/Containers/BuyButton.container')
        });
        Array.from(document.querySelectorAll('buy-button')).forEach((button) => {
            const { articleNumber, quantityFieldId, href, cssClass, label } = button.dataset;
            let ancestor = button.closest('.product-detail__buy-button-wrapper');
            if (!ancestor) {
                ancestor = button.closest('.product__quantity-container');
            }
            const { bigPackRadioId } = ancestor ? ancestor.dataset : 'notfound';
            renderReact(
                <Provider store={store}>
                    <BuyButton {...{ label, articleNumber, quantityFieldId, href, cssClass, bigPackRadioId }} />
                </Provider>,
                button
            );
        });
    }

    if (document.querySelectorAll('mypages-menu-and-heading').length > 0) {
        const MyPagesHamburgerMenu = DynamicComponent({
            loader: () => import('./Containers/MyPagesHamburgerMenu.container'),
        });

        Array.from(document.querySelectorAll('mypages-menu-and-heading')).forEach((el) => {
            let contentLinks = [];
            try {
                contentLinks = JSON.parse(el.dataset.contentLinks || '[]');
                if (!Array.isArray(contentLinks)) contentLinks = [];
            } catch (e) {
                console.error('mypages-hamburgermenu: invalid data-content-links JSON', e);
            }

            const currentItem = contentLinks.find((x) => x && x.current);
            const title = el.dataset.title || '';
            const activeLink = (currentItem && currentItem.name) || '';
            const activeText = (currentItem && currentItem.href) || '';

            renderReact(
                <Provider store={store}>
                    <MyPagesHamburgerMenu
                        title={title}
                        activeLink={activeLink}
                        activeText={activeText}
                        contentLinks={contentLinks}
                    />
                </Provider>,
                el
            );
        });
    }




    if (document.querySelectorAll('wishlist-button').length > 0) {
        const WishlistButton = DynamicComponent({
            loader: () => import('./Containers/WishlistButton.container')
        });
        Array.from(document.querySelectorAll('wishlist-button')).forEach((button) => {
            const { articleNumber, quantityFieldId, cssClass, inWishlist, bigpackRadioId, articleName } = button.dataset;
            renderReact(
                <Provider store={store}>
                    <WishlistButton
                        {...{ articleNumber, quantityFieldId, cssClass, bigpackRadioId, articleName }}
                        isInWishlist={String(inWishlist).toLowerCase() === 'true'}
                    />
                </Provider>,
                button
            );
        });
    }

    if (document.querySelectorAll('wishlist-quantity-selector').length > 0) {
        const WishlistQuantity = DynamicComponent({
            loader: () => import('./Containers/WishlistQuantity.container') 
        });

        Array.from(document.querySelectorAll('wishlist-quantity-selector')).forEach((el) => {
            const { articleNumber, quantityFieldId, cssClass, initialQuantity } = el.dataset;

            renderReact(
                <Provider store={store}>
                    <WishlistQuantity
                        articleNumber={articleNumber}
                        quantityFieldId={quantityFieldId}
                        cssClass={cssClass}
                        initialQuantity={parseInt(initialQuantity || '1', 10)}
                    />
                </Provider>,
                el
            );
        });
    }

    if (document.querySelectorAll('wishlist-add-all-to-cart').length > 0) {
        const WishlistAddAllToCartButton = DynamicComponent({
            loader: () => import('./Containers/WishlistAddAllToCartButton.container')
        });
        Array.from(document.querySelectorAll('wishlist-add-all-to-cart')).forEach((button) => {
            const { label, cssClass} = button.dataset;
            renderReact(
                <Provider store={store}>
                    <WishlistAddAllToCartButton label={label} cssClass={cssClass} />
                </Provider>,
                button
            );
        });
    }

    if (document.querySelectorAll('wishlist-header').length > 0) {
        const WishlistHeader = DynamicComponent({
            loader: () => import('./Containers/WishlistHeader.container')
        });

        Array.from(document.querySelectorAll('wishlist-header')).forEach((el) => {
            const { wishlistHref } = el.dataset;
            renderReact(
                <Provider store={store}>
                    <WishlistHeader wishlistHref={wishlistHref} />
                </Provider>,
                el
            );
        });
    } 

    if (document.querySelectorAll('is-bigpack-checked').length > 0) {
        const WishlistBigpackChecked = DynamicComponent({
            loader: () => import('./Containers/WishlistIsBigpackChecked.container')
        });

        Array.from(document.querySelectorAll('is-bigpack-checked')).forEach((el) => {
            const { articleNumber, quantityFieldId, bigpackRadioId } = el.dataset;

            renderReact(
                <Provider store={store}>
                    <WishlistBigpackChecked
                        hostEl={el}
                        articleNumber={articleNumber}
                        quantityFieldId={quantityFieldId}
                        bigpackRadioId={bigpackRadioId}
                    />
                </Provider>,
                el
            );
        });
    }

    if (document.querySelectorAll('reorder-button').length > 0) {
        const ReorderButton = DynamicComponent({
            loader: () => import('./Containers/ReorderButton.Container')
        });
        Array.from(document.querySelectorAll('reorder-button')).forEach((button) => {
            const { cssClass, orderId, title } = button.dataset;
            const label = button.innerText;
            renderReact(
                <Provider store={store}>
                    <ReorderButton {...{ label, title, cssClass, orderId }} />
                </Provider>,
                button
            );
        });
    }

    if (document.getElementById('org-picker-root')) {
        const OrgPicker = DynamicComponent({
            loader: () => import('./Containers/OrgPicker.container')
        });

        const el = document.getElementById('org-picker-root');
        const props = {
            formId: el.dataset.formId,
            hiddenId: el.dataset.hiddenId,
            initial: el.dataset.initial,
            label: el.dataset.label,
            options: JSON.parse(el.dataset.options || '[]'),
            autoSubmit: false 
        };

        renderReact(
            <Provider store={store}>
                <OrgPicker {...props} />
            </Provider>,
            el
        );
    }


  if (document.getElementById('js-cart')) {
    const CartComponent = DynamicComponent({
      loader: () => import('./JobMeal/Components/Cart')
    })

    const domElement = document.getElementById('js-cart')
    const orderTotal = domElement.dataset.total;
    const deliveryMethod = domElement.dataset.delivery ?
      JSON.parse(domElement.dataset.delivery) : undefined;
    const rowsData = domElement.dataset.rows ?
      JSON.parse(domElement.dataset.rows) : undefined;

    ReactDOM.render(
      <Provider store={store}>
        <CartComponent
          orderRows={rowsData}
          orderTotal={orderTotal}
          deliveryMethod={deliveryMethod}
          limitedView={true}
          canModify={false}
        />
      </Provider>,
      domElement
    )
  }
}



const bootstrapBuyButtons = (node, tagAsAddOnSale = false) =>  
{
    if (node.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./JobMeal/Containers/BuyButton.container')
        });
        Array.from(node.querySelectorAll('buy-button')).forEach((button) => {
            const { articleNumber, quantityFieldId, href, cssClass, label } = button.dataset;
            let ancestor = button.closest('.product-detail__buy-button-wrapper');
            if (!ancestor) {
                ancestor = button.closest('.product__quantity-container');
            }
            const { bigPackRadioId } = ancestor ? ancestor.dataset : 'notfound';
            renderReact(
                <Provider store={store}>
                    <BuyButton {...{ label, articleNumber, quantityFieldId, href, cssClass, tagAsAddOnSale, bigPackRadioId }} />
                </Provider>,
                button
            );
        });
    }
}

bootstrapComponents();

if (!window.frameElement) {
    window._sid = '2496';
    (function () {
        var se = document.createElement('script'); se.type = 'text/javascript'; se.async = true;
        se.src = ('https:' == document.location.protocol ? 'https://' : 'http://') + 'files.imbox.io/app/dist/initWidget.js';
        var s = document.getElementsByTagName('script')[0]; s.parentNode.insertBefore(se, s);
    })();
}